import { useCallback, useEffect, useRef, useState } from "react";

import { UsersApiHelperForMember } from "api/ApiHelper";

import { PAYMENT_SNOOZE_THRESHOLD_MINUTES, PAYMENT_TIMESTAMP_THRESHOLD_MINUTES } from "../config/constants";

export const useSubscription = (activeProducer, globalState) => {
  const [openInvoices, setOpenInvoices] = useState([]);
  const [doShowPaymentDialog, setDoShowPaymentDialog] = useState(false);
  const [isLoadingPaymentLink, setIsLoadingPaymentLink] = useState(false);
  const [isLoadingPaperCheck, setIsLoadingPaperCheck] = useState(false);

  // Track if subscription data has been loaded to prevent duplicate requests
  const hasLoadedRef = useRef(false);
  const producerIdRef = useRef(null);

  // Store timestamps in refs to access their latest values in callbacks without re-creating them
  const paymentTimestampRef = useRef(globalState?.subscription_payment_timestamp);
  const snoozeTimestampRef = useRef(globalState?.subscription_snooze_timestamp);

  // Update refs when the actual values change
  useEffect(() => {
    paymentTimestampRef.current = globalState?.subscription_payment_timestamp;
  }, [globalState?.subscription_payment_timestamp]);

  useEffect(() => {
    snoozeTimestampRef.current = globalState?.subscription_snooze_timestamp;
  }, [globalState?.subscription_snooze_timestamp]);

  // Calculate time difference since last payment in minutes
  const calculatePaymentTimestampDifference = useCallback(() => {
    try {
      return (
        (Boolean(paymentTimestampRef.current) && (new Date().getTime() - paymentTimestampRef.current) / 1000 / 60) || 0
      );
    } catch {
      return 0;
    }
  }, []);

  // Calculate time difference since last snooze in minutes
  const calculatePaymentSnoozeTimestampDifference = useCallback(() => {
    try {
      return (
        (Boolean(snoozeTimestampRef.current) && (new Date().getTime() - snoozeTimestampRef.current) / 1000 / 60) || 0
      );
    } catch {
      return 0;
    }
  }, []);

  // Store stable references to calculation functions
  // This pattern prevents infinite loops in useEffect dependencies by ensuring
  // the function references remain stable while still accessing the latest state
  const stableCalculatePaymentTimestampDifference = useRef(calculatePaymentTimestampDifference);
  const stableCalculatePaymentSnoozeTimestampDifference = useRef(calculatePaymentSnoozeTimestampDifference);

  // Update the stable refs whenever the original functions change
  useEffect(() => {
    stableCalculatePaymentTimestampDifference.current = calculatePaymentTimestampDifference;
  }, [calculatePaymentTimestampDifference]);

  useEffect(() => {
    stableCalculatePaymentSnoozeTimestampDifference.current = calculatePaymentSnoozeTimestampDifference;
  }, [calculatePaymentSnoozeTimestampDifference]);

  // Store props in refs to access their latest values in callbacks
  const activeProducerRef = useRef(activeProducer);
  const globalStateRef = useRef(globalState);

  useEffect(() => {
    activeProducerRef.current = activeProducer;
  }, [activeProducer]);

  useEffect(() => {
    globalStateRef.current = globalState;
  }, [globalState]);

  const loadOpenInvoices = useCallback(async () => {
    const currentProducer = activeProducerRef.current;

    if (
      !currentProducer?.subscription_is_posted ||
      (producerIdRef.current === currentProducer?.id && hasLoadedRef.current)
    ) {
      return;
    }

    producerIdRef.current = currentProducer?.id;

    try {
      const sales = await UsersApiHelperForMember.callPost(
        "Member/subscription/unpaid/search",
        {
          id: currentProducer?.id,
          producer_token: currentProducer?.producer_token,
          agent_token: currentProducer?.agent_token,
        },
        globalStateRef.current?.globalState || globalStateRef.current
      );

      const invoices = Array.isArray(sales) ? sales : [];
      setOpenInvoices(invoices);
      hasLoadedRef.current = true;

      if (invoices.length > 0) {
        const paymentDiff = stableCalculatePaymentTimestampDifference.current();
        const snoozeDiff = stableCalculatePaymentSnoozeTimestampDifference.current();

        if (
          (paymentDiff === 0 || paymentDiff > PAYMENT_TIMESTAMP_THRESHOLD_MINUTES) &&
          (snoozeDiff === 0 || snoozeDiff > PAYMENT_SNOOZE_THRESHOLD_MINUTES)
        ) {
          setDoShowPaymentDialog(true);
        }
      }
    } catch (err) {
      console.warn("Error loading open invoices:", err);
    }
  }, []);

  // Trigger subscription status loading when producer changes or subscription status updates
  useEffect(() => {
    if (producerIdRef.current !== activeProducer?.id) {
      hasLoadedRef.current = false;
    }

    if (activeProducer?.subscription_is_posted || activeProducer?.subscription_is_posted === false) {
      loadOpenInvoices();
    }
  }, [activeProducer?.id, activeProducer?.subscription_is_posted, loadOpenInvoices]);

  return {
    openInvoices,
    doShowPaymentDialog,
    isLoadingPaymentLink,
    isLoadingPaperCheck,
    setDoShowPaymentDialog,
    setIsLoadingPaymentLink,
    setIsLoadingPaperCheck,
  };
};
